import { MODULES } from '@/app-constants/module.constants';
import { PERMISSIONS } from '@/app-constants/permission.constants';
import { AccessPolicies, AccessPolicy, AccessPolicyType, PermissionScopeEnum } from '@/entities/role.entity';

export type RouteItem = {
  KEY: string;
  NAME: string;
  PATH: string;
  TITLE?: string;
  accessPolicy?: AccessPolicy;
};

export const ROUTER = {
  APP_NAME: 'Digital Hands Customer Portal',
  BASE_URL: '/',
  AUTH_CALLBACK: {
    KEY: 'auth-callback',
    NAME: 'Auth Callback',
    PATH: '/auth/callback',
  },
  AUTH_SIGNUP: {
    KEY: 'auth-signup',
    NAME: 'Sign Up',
    PATH: '/auth/signup',
  },
  AUTH_EMAIL_VERIFY: {
    KEY: 'auth-email-verify',
    NAME: 'Email Verify',
    PATH: '/auth/email-verify',
  },
  AUTH_ACCOUNT_INACTIVATED: {
    KEY: 'auth-account-inactivated',
    NAME: 'Account Inactivated',
    PATH: '/auth/account-inactivated',
  },
  CUSTOMER: {
    HOME_PAGE: {
      KEY: 'home',
      NAME: 'Home',
      TITLE: 'Digital Hands Customer Portal',
      PATH: '/',
      accessPolicy: {
        moduleName: MODULES.HOME.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_PAGE: {
      KEY: 'dashboards',
      NAME: 'Dashboards',
      PATH: '/dashboards',
    },
    DASHBOARDS_SIEM_PAGE: {
      KEY: 'dashboards-siem',
      NAME: 'Google SecOps SIEM',
      TITLE: 'Google SecOps SIEM',
      PATH: '/dashboards/cyguard-cloud-siem',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_SIEM.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_MITRE_PAGE: {
      KEY: 'dashboards-mitre',
      NAME: 'MITRE ATT&CK Coverage',
      TITLE: 'MITRE ATT&CK Coverage',
      PATH: '/dashboards/mitre-dashboard',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_MITRE.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_EDR_PAGE: {
      KEY: 'dashboards-edr',
      NAME: 'SentinelOne EDR',
      TITLE: 'SentinelOne EDR',
      PATH: '/dashboards/cyguard-edr',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_EDR.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_EDR_CROWDSTRIKE_PAGE: {
      KEY: 'dashboards-edr-crowdstrike',
      NAME: 'CrowdStrike EDR',
      TITLE: 'CrowdStrike EDR',
      PATH: '/dashboards/cyguard-edr-crowdstrike',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_EDR_CROWDSTRIKE.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_CASE_PAGE: {
      KEY: 'dashboards-case',
      NAME: 'Case Insights',
      TITLE: 'Case Insights',
      PATH: '/dashboards/cases',
      accessPolicy: {
        moduleName: MODULES.CASES.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_VULNERABILITY_PAGE: {
      KEY: 'dashboards-vulnerability',
      NAME: 'Fortra Vulnerability Management',
      TITLE: 'Fortra Vulnerability Management',
      PATH: '/dashboards/vulnerability',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_VULNERABILITY.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_CISO_PAGE: {
      KEY: 'dashboards-ciso',
      NAME: 'CISO',
      TITLE: 'CISO',
      PATH: '/dashboards/ciso',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_CISO.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_PATCH_MANAGEMENT_PAGE: {
      KEY: 'dashboards-patch-management',
      NAME: 'Patch Management',
      TITLE: 'Patch Management',
      PATH: '/dashboards/patch-management',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_PATCH_MANAGEMENT.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    DASHBOARDS_PROMPT_AI_PAGE: {
      KEY: 'dashboards-prompt-ai',
      NAME: 'Prompt AI',
      TITLE: 'Prompt AI',
      PATH: '/dashboards/prompt-ai',
      accessPolicy: {
        moduleName: MODULES.DASHBOARDS_PROMPT_AI.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    FILES_PAGE: {
      KEY: 'files',
      NAME: 'File Center',
      TITLE: 'File Center',
      PATH: '/files',
      accessPolicy: {
        moduleName: MODULES.FILE_CENTER.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    ACTIVITIES_PAGE: {
      KEY: 'activities',
      NAME: 'Activities',
      TITLE: 'Service Activities',
      PATH: '/activities',
      accessPolicy: {
        moduleName: MODULES.ACTIVITIES.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    MY_LINKS_PAGE: {
      KEY: 'my-links',
      NAME: 'My Links',
      TITLE: 'My Links',
      PATH: '/my-links',
      accessPolicy: {
        moduleName: MODULES.MY_LINKS.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    SERVICE_PAGE: {
      KEY: 'services',
      NAME: 'Services',
      TITLE: 'Services Information',
      PATH: '/services',
      accessPolicy: {
        moduleName: MODULES.SERVICES.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    NOTIFICATION_PAGE: {
      KEY: 'notifications',
      NAME: 'Notifications',
      TITLE: 'Notifications',
      PATH: '/notifications',
      accessPolicy: {
        moduleName: MODULES.NOTIFICATION.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    CONTACT_US_PAGE: {
      KEY: 'contact-us',
      NAME: 'Contact Us',
      TITLE: 'Contact Us',
      PATH: '/contact-us',
      accessPolicy: {
        moduleName: MODULES.CONTACT_US.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    SECURITY_POSTURE_SURVEY_TENANT_PAGE: {
      KEY: 'security-posture-survey-tenant',
      NAME: 'Security Posture Survey',
      TITLE: 'Security Posture Survey',
      PATH: '/security-posture-survey/t/:surveyTenantId',
      accessPolicy: {
        moduleName: MODULES.SECURITY_POSTURE.moduleName,
        permission: PERMISSIONS.UPDATE,
        scope: PermissionScopeEnum.self,
      },
    },
    MY_CASES_PAGE: {
      KEY: 'my-cases',
      NAME: 'My Cases',
      TITLE: 'My Cases',
      PATH: '/my-cases',
      accessPolicy: {
        moduleName: MODULES.MY_CASES.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    SERVICENOW_DETAIL_PAGE: {
      KEY: 'servicenow',
      NAME: 'ServiceNow',
      TITLE: 'Case',
      PATH: '/my-cases/:id',
      accessPolicy: {
        moduleName: MODULES.MY_CASES.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    USER_MANAGEMENT_PAGE: {
      KEY: 'user-management',
      NAME: 'User Management',
      TITLE: 'User Management',
      PATH: '/user-management',
      accessPolicy: {
        moduleName: MODULES.USER_MANAGEMENT.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
  },
  PARTNER: {
    LANDING_PAGE: {
      KEY: 'partner-landing-page',
      NAME: 'Home',
      TITLE: 'Digital Hands Customer Portal',
      PATH: '/partner',
      accessPolicy: {
        moduleName: MODULES.PARTNER_LANDING_PAGE.moduleName,
        permission: PERMISSIONS.READ,
        scope: PermissionScopeEnum.self,
      },
    },
    CUSTOMER: {
      HOME_PAGE: {
        KEY: 'partner-tenant-home',
        NAME: 'Home',
        TITLE: 'Digital Hands Customer Portal',
        PATH: '/partner/tenants/:tenantId',
        accessPolicy: {
          moduleName: MODULES.HOME.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_PAGE: {
        KEY: 'partner-tenant-dashboards',
        NAME: 'Dashboards',
        PATH: '/partner/tenants/:tenantId/dashboards',
      },
      DASHBOARDS_SIEM_PAGE: {
        KEY: 'partner-tenant-dashboards-siem',
        NAME: 'Google SecOps SIEM',
        TITLE: 'Google SecOps SIEM',
        PATH: '/partner/tenants/:tenantId/dashboards/cyguard-cloud-siem',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_SIEM.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_MITRE_PAGE: {
        KEY: 'partner-tenant-dashboards-mitre',
        NAME: 'MITRE ATT&CK Coverage',
        TITLE: 'MITRE ATT&CK Coverage',
        PATH: '/partner/tenants/:tenantId/dashboards/mitre-dashboard',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_MITRE.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_EDR_PAGE: {
        KEY: 'partner-tenant-dashboards-edr',
        NAME: 'SentinelOne EDR',
        TITLE: 'SentinelOne EDR',
        PATH: '/partner/tenants/:tenantId/dashboards/cyguard-edr',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_EDR.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_EDR_CROWDSTRIKE_PAGE: {
        KEY: 'partner-tenant-dashboards-edr-crowdstrike',
        NAME: 'CrowdStrike EDR',
        TITLE: 'CrowdStrike EDR',
        PATH: '/partner/tenants/:tenantId/dashboards/cyguard-edr-crowdstrike',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_EDR_CROWDSTRIKE.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_CASE_PAGE: {
        KEY: 'partner-tenant-dashboards-case',
        NAME: 'Case Insights',
        TITLE: 'Case Insights',
        PATH: '/partner/tenants/:tenantId/dashboards/cases',
        accessPolicy: {
          moduleName: MODULES.CASES.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_VULNERABILITY_PAGE: {
        KEY: 'partner-dashboards-vulnerability',
        NAME: 'Fortra Vulnerability Management',
        TITLE: 'Fortra Vulnerability Management',
        PATH: '/partner/tenants/:tenantId/dashboards/vulnerability',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_VULNERABILITY.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_CISO_PAGE: {
        KEY: 'partner-dashboards-ciso',
        NAME: 'CISO',
        TITLE: 'CISO',
        PATH: '/partner/tenants/:tenantId/dashboards/ciso',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_CISO.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_PATCH_MANAGEMENT_PAGE: {
        KEY: 'partner-dashboards-patch-management',
        NAME: 'Patch Management',
        TITLE: 'Patch Management',
        PATH: '/partner/tenants/:tenantId/dashboards/patch-management',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_PATCH_MANAGEMENT.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      DASHBOARDS_PROMPT_AI_PAGE: {
        KEY: 'partner-dashboards-prompt-ai',
        NAME: 'Prompt AI',
        TITLE: 'Prompt AI',
        PATH: '/partner/tenants/:tenantId/dashboards/prompt-ai',
        accessPolicy: {
          moduleName: MODULES.DASHBOARDS_PROMPT_AI.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      FILES_PAGE: {
        KEY: 'partner-tenant-files',
        NAME: 'File Center',
        TITLE: 'File Center',
        PATH: '/partner/tenants/:tenantId/files',
        accessPolicy: {
          moduleName: MODULES.FILE_CENTER.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      ACTIVITIES_PAGE: {
        KEY: 'partner-tenant-activities',
        NAME: 'Activities',
        TITLE: 'Service Activities',
        PATH: '/partner/tenants/:tenantId/activities',
        accessPolicy: {
          moduleName: MODULES.ACTIVITIES.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      MY_LINKS_PAGE: {
        KEY: 'partner-tenant-my-links',
        NAME: 'My Links',
        TITLE: 'My Links',
        PATH: '/partner/tenants/:tenantId/my-links',
        accessPolicy: {
          moduleName: MODULES.MY_LINKS.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      SERVICE_PAGE: {
        KEY: 'partner-tenant-services',
        NAME: 'Services',
        TITLE: 'Services Information',
        PATH: '/partner/tenants/:tenantId/services',
        accessPolicy: {
          moduleName: MODULES.SERVICES.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      CONTACT_US_PAGE: {
        KEY: 'partner-tenant-contact-us',
        NAME: 'Contact Us',
        TITLE: 'Contact Us',
        PATH: '/partner/tenants/:tenantId/contact-us',
        accessPolicy: {
          moduleName: MODULES.CONTACT_US.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.self,
        },
      },
      SECURITY_POSTURE_SURVEY_TENANT_PAGE: {
        KEY: 'partner-tenant-security-posture-survey',
        NAME: 'Security Posture Survey',
        TITLE: 'Security Posture Survey',
        PATH: '/partner/tenants/:tenantId/security-posture-survey/t/:surveyTenantId',
        accessPolicy: {
          type: AccessPolicyType.and,
          policies: [
            {
              moduleName: MODULES.SECURITY_POSTURE.moduleName,
              permission: PERMISSIONS.UPDATE,
              scope: PermissionScopeEnum.related,
            },
            {
              moduleName: MODULES.SECURITY_POSTURE.moduleName,
              permission: PERMISSIONS.READ,
              scope: PermissionScopeEnum.related,
            },
          ],
        },
      },
      MY_CASES_PAGE: {
        KEY: 'partner-tenant-my-cases',
        NAME: 'My Cases',
        TITLE: 'My Cases',
        PATH: '/partner/tenants/:tenantId/my-cases',
        accessPolicy: {
          moduleName: MODULES.MY_CASES.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      SERVICENOW_DETAIL_PAGE: {
        KEY: 'partner-tenant-servicenow',
        NAME: 'ServiceNow',
        TITLE: 'Case',
        PATH: '/partner/tenants/:tenantId/my-cases/:id',
        accessPolicy: {
          moduleName: MODULES.MY_CASES.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
      USER_MANAGEMENT_PAGE: {
        KEY: 'partner-tenant-user-management',
        NAME: 'User Management',
        TITLE: 'User Management',
        PATH: '/partner/tenants/:tenantId/user-management',
        accessPolicy: {
          moduleName: MODULES.USER_MANAGEMENT.moduleName,
          permission: PERMISSIONS.READ,
          scope: PermissionScopeEnum.related,
        },
      },
    },
  },
  ADMIN: {
    HOME_PAGE: {
      KEY: 'admin-home',
      NAME: 'Home',
      TITLE: 'Digital Hands Admin Portal',
      PATH: '/admin',
      accessPolicy: {
        moduleName: MODULES.HOME.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    TENANTS_PAGE: {
      KEY: 'admin-tenants',
      NAME: 'Tenants',
      TITLE: 'Tenant Management',
      PATH: '/admin/tenants',
    },
    FILES_PAGE: {
      KEY: 'admin-files',
      NAME: 'File Center',
      TITLE: 'File Center',
      PATH: '/admin/files',
      accessPolicy: {
        moduleName: MODULES.FILE_CENTER.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    CSM_PAGE: {
      KEY: 'admin-csm',
      NAME: 'Internal Admin',
      TITLE: 'Internal User Administration',
      PATH: '/admin/csm',
      accessPolicy: {
        moduleName: MODULES.INTERNAL_ADMIN.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    CSM_MANAGED_CUSTOMERS_PAGE: {
      KEY: 'admin-csm-managed-customers',
      NAME: "CSM's Managed Customers",
      TITLE: "CSM's Managed Customers",
      PATH: '/admin/csm/:id',
      accessPolicy: {
        type: AccessPolicyType.or,
        policies: [
          {
            moduleName: MODULES.INTERNAL_ADMIN.moduleName,
            permission: PERMISSIONS.CREATE,
          },
          {
            moduleName: MODULES.INTERNAL_ADMIN.moduleName,
            permission: PERMISSIONS.DELETE,
          },
        ],
      } as AccessPolicies,
    },
  },
  REPORT_RENDER: {
    KEY: 'report-render',
    NAME: 'Report Render',
    PATH: '/report-render',
  },
  REPORT_SCHEDULES_PAGE: {
    KEY: 'report-schedules',
    NAME: 'Report Schedules',
    TITLE: 'Report Schedules',
    PATH: '/report-schedules',
    accessPolicy: {
      moduleName: MODULES.PDF_REPORTS.moduleName,
      permission: PERMISSIONS.READ,
      scope: PermissionScopeEnum.self,
    } as AccessPolicy,
  },
  REPORT_HISTORY_PAGE: {
    KEY: 'report-history',
    NAME: 'Report History',
    TITLE: 'Report History',
    PATH: '/report-history',
    accessPolicy: {
      moduleName: MODULES.PDF_REPORTS.moduleName,
      permission: PERMISSIONS.READ,
      scope: PermissionScopeEnum.self,
    } as AccessPolicy,
  },
  ADMIN_TENANT: {
    HOME_PAGE: {
      KEY: 'admin-tenant-home',
      TITLE: 'Tenant Admin',
      DYNAMIC_TITLE: '{tenantName}',
      NAME: 'Tenant Home',
      PATH: '/admin/tenants/:tenantId',
      accessPolicy: {
        moduleName: MODULES.TENANT_HOME.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    LOGS_PAGE: {
      KEY: 'admin-tenant-logs',
      TITLE: 'Audit Logs - Tenant Admin',
      DYNAMIC_TITLE: 'Audit Logs - {tenantName}',
      NAME: 'Audit Logs',
      PATH: '/admin/tenants/:tenantId/logs',
      accessPolicy: {
        moduleName: MODULES.AUDIT_LOGS.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    FILES_PAGE: {
      KEY: 'admin-tenant-files',
      TITLE: 'File Center - Tenant Admin',
      DYNAMIC_TITLE: 'File Center - {tenantName}',
      NAME: 'File Center',
      PATH: '/admin/tenants/:tenantId/files',
      accessPolicy: {
        moduleName: MODULES.TENANT_FILE_CENTER.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
    SECURITY_POSTURE_PAGE: {
      KEY: 'admin-tenant-security-posture',
      TITLE: 'Security Posture - Tenant Admin',
      DYNAMIC_TITLE: 'Security Posture - {tenantName}',
      NAME: 'Security Posture',
      PATH: '/admin/tenants/:tenantId/security-posture',
      accessPolicy: {
        moduleName: MODULES.SECURITY_POSTURE.moduleName,
        permission: PERMISSIONS.READ,
      },
    },
  },
};
